import React, { forwardRef } from 'react'

import classNames from 'classnames/dedupe'
import PropTypes from 'prop-types'

import { modsToClassnames } from 'helpers/classname.js'

import './ColumnarLayout.css'

/**
 * @typedef {Object} ColumnProps
 * @property {string} [className] - Additional CSS class names
 * @property {number} [size=1] - Size of the column (affects flex-grow)
 * @property {boolean} [sticky] - Whether the column should be sticky
 * @property {Object} [mods] - Modifiers for styling
 * @property {React.ReactNode} children - Column content
 */

/**
 * A single column within a ColumnarLayout
 * @param {ColumnProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {JSX.Element}
 */
/** @type {React.ForwardRefExoticComponent<ColumnProps & React.RefAttributes<HTMLDivElement>>} */
const Column = forwardRef(({ className, size = 1, sticky, mods, children, ...rest }, ref) => {
  const classes = classNames(
    className,
    'ColumnarLayout-Column',
    [`ColumnarLayout-Column_size_${size}`],
    modsToClassnames('ColumnarLayout-Column', mods),
    { 'ColumnarLayout-Column_sticky': sticky },
  )

  return (
    <div ref={ref} className={classes} {...rest}>
      {children}
    </div>
  )
})
Column.displayName = 'Column'

Column.propTypes = {
  className: PropTypes.string,
  size: PropTypes.number,
  sticky: PropTypes.bool,
  mods: PropTypes.object,
  children: PropTypes.node,
}

/**
 * @typedef {Object} ColumnarLayoutProps
 * @property {string} [className] - Additional CSS class names
 * @property {Object} [mods] - Modifiers for styling
 * @property {React.ReactNode} children - Layout content
 * @property {boolean} [inline] - Whether to display inline
 * @property {boolean} [flexible] - Whether the layout should be flexible
 * @property {boolean} [fluid] - Whether the layout should be fluid
 * @property {boolean} [bordered] - Whether to show borders
 * @property {boolean} [resetBasis] - Whether to reset flex basis
 * @property {'column'|'column-reverse'} [flexDirection] - Flex direction
 * @property {React.CSSProperties} [style] - Additional inline styles
 * @property {React.Ref<HTMLDivElement>} [forwardedRef] - Forwarded ref
 */

/**
 * A flexible column-based layout component that can arrange its children in columns
 * Automatically wraps non-Column children in Column components
 * @param {ColumnarLayoutProps} props
 * @returns {JSX.Element}
 */
const ColumnarLayout = ({
  className,
  mods,
  children,
  inline,
  flexible,
  fluid,
  bordered,
  resetBasis,
  flexDirection,
  style = {},
  forwardedRef,
  ...rest
}) => {
  const classes = classNames(
    className,
    'ColumnarLayout',
    {
      ColumnarLayout_inline: inline,
      ColumnarLayout_flexible: flexible,
      ColumnarLayout_fluid: fluid,
      ColumnarLayout_bordered: bordered,
      ColumnarLayout_resetBasis: resetBasis,
      [`ColumnarLayout_flexDirection_${flexDirection}`]: flexDirection,
    },
    modsToClassnames('ColumnarLayout', mods),
  )

  style = {
    ...style,
    flexDirection,
  }

  return (
    <div className={classes} style={style} {...rest} ref={forwardedRef}>
      {React.Children.map(children, (child) => {
        if (child === null || Column === child.type) {
          return child
        }

        return <Column>{child}</Column>
      })}
    </div>
  )
}

const propTypes = (ColumnarLayout.propTypes = {
  className: PropTypes.string,
  mods: PropTypes.object,
  children: PropTypes.node,
  inline: PropTypes.bool,
  flexible: PropTypes.bool,
  fluid: PropTypes.bool,
  bordered: PropTypes.bool,
  resetBasis: PropTypes.bool,
  flexDirection: PropTypes.oneOf(['column', 'column-reverse']),
  style: PropTypes.object,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
})

export { Column, propTypes }
export default ColumnarLayout
